import { CdkPortal, PortalModule } from '@angular/cdk/portal';
import { AfterViewInit, DestroyRef, ViewChild } from '@angular/core';
import { Overlay, OverlayContainer, OverlayRef } from '@angular/cdk/overlay';
import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID } from '@angular/core';
import { A11yModule } from '@angular/cdk/a11y';
import { ChangeDetectionStrategy, Component, LOCALE_ID, computed, effect, inject } from '@angular/core';
import { MaintenanceService } from './maintenance.service';
@Component({
  selector: 'hb-maintenance-notice',
  imports: [A11yModule, PortalModule],
  templateUrl: './maintenance-notice.component.html',
  styleUrl: './maintenance-notice.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MaintenanceNoticeComponent implements AfterViewInit {
  @ViewChild(CdkPortal, { static: true }) private portal!: CdkPortal;
  private readonly destroyRef = inject(DestroyRef);
  readonly maintenance = inject(MaintenanceService);
  private readonly locale = inject(LOCALE_ID);
  private readonly overlays = inject(OverlayContainer);
  private readonly overlay = inject(Overlay);
  private layer: OverlayRef | null = null;
  private readonly previousInert = new Map<HTMLElement, boolean>();
  private readonly browser = isPlatformBrowser(inject(PLATFORM_ID));
  readonly visible = computed(() => this.maintenance.blocked() || this.maintenance.state().mode === 'connection');
  readonly title = computed(() => {
    const s = this.maintenance.state();
    if (s.mode === 'recovery') {
      return $localize`:@@maintenanceRecovery:Waiting for the service to recover`;
    }
    if (s.blocked) {
      return $localize`:@@maintenanceTitle:Scheduled maintenance`;
    }
    if (!this.maintenance.online()) {
      return $localize`:@@maintenanceOffline:You are offline`;
    }
    return $localize`:@@maintenanceConnection:Unable to connect to HobbyTS`;
  });
  readonly message = computed(
    () => this.maintenance.state().config?.message[this.locale.startsWith('uk') ? 'uk' : 'en']
  );
  readonly eta = computed(() => {
    const value = this.maintenance.state().config?.expectedBackAt;
    return value ? new Date(value).toLocaleString(this.locale) : null;
  });
  constructor() {
    effect(() => {
      this.syncInert(this.maintenance.blocked());
      if (this.visible()) {
        this.raiseLayer();
      }
    });
  }
  private syncInert(blocked: boolean): void {
    if (!this.browser || !this.layer) {
      return;
    }
    if (!blocked) {
      for (const [element, inert] of this.previousInert) {
        element.inert = inert;
      }
      this.previousInert.clear();
      return;
    }
    for (const element of Array.from(this.overlays.getContainerElement().children)) {
      if (element instanceof HTMLElement && element !== this.layer.hostElement) {
        if (!this.previousInert.has(element)) {
          this.previousInert.set(element, element.inert);
        }
        element.inert = true;
      }
    }
  }
  private raiseLayer(): void {
    const host = this.layer?.hostElement;
    if (!host || typeof host.showPopover !== 'function' || !host.matches(':popover-open')) {
      return;
    }
    const focused = document.activeElement;
    // Each CDK overlay has its own native top-layer popover; z-index cannot order them.
    host.hidePopover();
    host.showPopover();
    if (focused instanceof HTMLElement && host.contains(focused)) {
      focused.focus();
    }
  }
  ngAfterViewInit(): void {
    if (!this.browser) {
      return;
    }
    // Share CDK's native-popover top layer while blocking only underlying dialogs.
    this.layer = this.overlay.create({ positionStrategy: this.overlay.position().global().top('0').left('0') });
    this.layer.hostElement.style.zIndex = '2000';
    this.layer.attach(this.portal);
    this.syncInert(this.maintenance.blocked());
    const keepAbove = (event: Event): void => {
      const element = event.target;
      if (
        this.visible() &&
        element instanceof HTMLElement &&
        element !== this.layer?.hostElement &&
        typeof element.showPopover === 'function' &&
        element.matches(':popover-open')
      ) {
        this.raiseLayer();
      }
    };
    const container = this.overlays.getContainerElement();
    container.addEventListener('toggle', keepAbove, true);
    if (this.visible()) {
      this.raiseLayer();
    }
    const observer = new MutationObserver(() => this.syncInert(this.maintenance.blocked()));
    observer.observe(this.overlays.getContainerElement(), { childList: true });
    this.destroyRef.onDestroy(() => {
      observer.disconnect();
      container.removeEventListener('toggle', keepAbove, true);
      this.syncInert(false);
      this.layer?.dispose();
    });
  }
  onKeydown(event: KeyboardEvent): void {
    if (!this.maintenance.blocked() || event.key !== 'Tab') {
      return;
    }
    const section = event.currentTarget;
    if (!(section instanceof HTMLElement)) {
      return;
    }
    // WebKit can skip native buttons/links according to system keyboard settings.
    // Cycle this dialog's explicit actions consistently while retaining CDK trapping.
    const controls = Array.from(section.querySelectorAll<HTMLElement>('button:not([disabled]), a[href]')).filter(
      (element) => element.getClientRects().length > 0
    );
    if (!controls.length) {
      return;
    }
    const current = controls.indexOf(document.activeElement as HTMLElement);
    const next =
      current < 0
        ? event.shiftKey
          ? controls.length - 1
          : 0
        : (current + (event.shiftKey ? -1 : 1) + controls.length) % controls.length;
    event.preventDefault();
    controls[next].focus();
  }
  retry(): void {
    void this.maintenance.check();
  }
}
